// FC spread view helpers (spec docs/superpowers/specs/2026-10-28-fc-spread-target-design.md §6.2).
// Pure — the page never re-derives spread, it only formats what the server sent.

const regionCount = (spread) => Object.keys(spread?.heldByRegion || {}).length;

export function regionsHeld(spread) {
  if (!spread || !spread.heldByRegion) return null;
  const held = Object.values(spread.heldByRegion).filter((n) => n > 0).length;
  return `${held}/${regionCount(spread)} regions`;
}

export function stockSpreadText(spread) {
  if (!spread || spread.status === 'UNKNOWN' || spread.heldFcs == null) return null;
  const r = regionsHeld(spread);
  return `In ${spread.heldFcs} FCs${r ? ` · ${r}` : ''}`;
}

export function missingText(spread) {
  return Object.entries(spread?.missingByRegion || {})
    .filter(([, n]) => n > 0)
    .map(([r, n]) => `${r} ×${n}`)
    .join(', ');
}

// Spec 2026-11-03 §7: the regional sizing behind the order — "Central 156 (16 held) · Southeast 132 · …".
// Server key order; regions needing nothing are left out; null when the row was not sized regionally.
const SIZED = ['COVERAGE', 'NATIONAL', 'OWN_SALES'];

export function coverageBreakdown(spread) {
  if (!spread || !SIZED.includes(spread.sizedBy) || !spread.regionNeed) return null;
  const parts = Object.entries(spread.regionNeed)
    .filter(([, n]) => n > 0)
    .map(([r, n]) => {
      const held = spread.regionHave?.[r] || 0;
      const dock = spread.regionDockAlloc?.[r] || 0;
      return `${r} ${n}${held > 0 ? ` (${held} held)` : ''}${dock > 0 ? ` (+${dock} from here)` : ''}`;
    });
  return parts.length ? parts.join(' · ') : null;
}

/** "Sized for coverage over 81 d at 6.6/day" / "Sized by own sales" / "Sized nationally (oversize)"; null otherwise. */
export function sizingSentence(spread) {
  switch (spread?.sizedBy) {
    case 'COVERAGE': {
      const over = spread.horizonDays != null ? ` over ${spread.horizonDays} d` : '';
      const rate = spread.rateDaily != null ? ` at ${Number(spread.rateDaily).toFixed(1)}/day` : '';
      return `Sized for coverage${over}${rate}`;
    }
    case 'OWN_SALES': return 'Sized by own sales';
    case 'NATIONAL': return 'Sized nationally (oversize)';
    default: return null;
  }
}

const sizingNote = (spread) => {
  const s = sizingSentence(spread);
  const b = coverageBreakdown(spread);
  return s && b ? `${s}: ${b}` : (s || b);
};

const coverageSized = (spread) => spread?.sizedBy === 'COVERAGE' && spread.coverageTotal != null;

export function spreadCell(spread) {
  if (!spread || !spread.status || spread.status === 'UNKNOWN') return { text: '—', sort: -1, title: 'Spread unknown' };
  if (spread.status === 'AT_TARGET') {
    if (coverageSized(spread)) {
      return { text: `At target · ${spread.coverageTotal}u`, sort: spread.coverageTotal,
        title: `In ${spread.heldFcs} FCs. ${sizingNote(spread)}` };
    }
    return { text: 'At target', sort: 0, title: `In ${spread.heldFcs} FCs` };
  }
  if (spread.status === 'OVERSIZE') return { text: 'Oversize', sort: -1, title: 'Oversize: Amazon controls placement' };
  if (spread.status === 'NO_RANK') return { text: `+${spread.missing} FCs · no rank`, sort: -1, title: 'No sales rank — spread units unknown' };
  if (spread.status === 'NO_BOX') return { text: `+${spread.missing} FCs · box qty not set`, sort: -1, title: 'Set the box to size spread units' };
  const losing = Object.entries(spread.speedLossRegions || {});
  const losingText = losing.map(([r, pct]) => (pct == null ? r : `${r} ${Math.round(pct)}%`)).join(', ');
  const units = coverageSized(spread) ? spread.coverageTotal : spread.spreadUnits;
  const note = coverageSized(spread) ? sizingNote(spread) : null;
  return {
    text: `+${spread.missing} FCs · ${units}u${losing.length ? ` · losing: ${losingText}` : ''}`,
    sort: units,
    title: `Missing ${missingText(spread)} — ${spread.perFcBoxed} per FC`
      // M4: per-region count of warehouses still missing (a region beyond target gets no further slot).
      + (losing.length ? `. Losing on speed: ${losing.map(([r]) => {
        const n = spread.missingByRegion?.[r];
        return n > 0 ? `${r} ×${n} still missing` : r;
      }).join(', ')}` : '')
      + (note ? `. ${note}` : ''),
  };
}

// Final review F3: the spread units the engine ADDED to the suggestion (net of spare on-order units, 0 under
// the low-margin guard) — never the gross spread.spreadUnits.
const netSpread = (row) => {
  const n = row?.spreadAddedUnits;
  return n == null ? null : Math.max(0, Number(n) || 0);
};

export function draftSpread(row, quantity, sfp) {
  const sp = row?.spread;
  const net = netSpread(row);
  if (!sp || sp.status !== 'OK' || !net) return 0;
  const room = Math.max(0, (Number(quantity) || 0) - (Number(sfp) || 0));
  return Math.min(net, room);
}

/** "depth X + spread Y" for a positive suggestion that sized spread units; null otherwise (e.g. low margin). */
export function spreadBreakdown(row) {
  // Coverage-sized rows are one regional quantity, not depth + spread: the breakdown line shows them instead.
  if (row?.sizedBy === 'COVERAGE' || row?.sizedBy === 'NATIONAL') return null;
  const net = netSpread(row);
  const qty = Number(row?.suggestedOrderQty) || 0;
  if (net == null || qty <= 0) return null;
  return { depth: Math.max(0, qty - net), spread: net };
}

const SEVERITY = ['OUT', 'CRITICAL', 'LOW'];

export function warningLevel(spread) {
  const w = spread?.worstLevel;
  return SEVERITY.includes(w) ? w : null;
}

export function warningText(spread) {
  const levels = spread?.regionLevel || {};
  const parts = [];
  SEVERITY.forEach((lvl) => Object.entries(levels).forEach(([r, l]) => {
    if (l !== lvl) return;
    parts.push(l === 'OUT' ? `${r} out` : `${r} ${Math.floor(spread.regionDays?.[r] ?? 0)} days`);
  }));
  return parts.join(', ');
}

export function spreadDetail(spread) {
  if (!spread || spread.status !== 'OK') return null;
  const regions = Object.keys(spread.missingByRegion || {}).length;
  const per = regions ? Math.round(spread.target / regions) : null;
  const out = { missingByRegion: spread.missingByRegion, perFcBoxed: spread.perFcBoxed, targetPerRegion: per };
  // Spec 2026-11-03 §7: keys only when sized, so pre-coverage PO lines keep their exact shape.
  const breakdown = coverageBreakdown(spread);
  const sizing = sizingSentence(spread);
  if (breakdown) out.breakdown = breakdown;
  if (sizing) out.sizing = sizing;
  return out;
}
